import React, { useEffect, useState } from 'react';
import { Select as SelectAntd, Switch, Tag } from 'antd';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';
import { Typography } from '_atoms/Typography';
import { ReactComponent as CheckMark } from 'dist/icons/checkmark.svg';
import { ReactComponent as Arrow } from 'dist/icons/caret-down.svg';
import { ReactComponent as Clear } from 'dist/icons/clear.svg';
import { SELECT_TYPES } from './constants';
import type { SelectOption, SelectOptionGroup } from './types';
import { isOptionGroup } from './utils';

type AntdSelectOptions = React.ComponentProps<typeof SelectAntd>['options'];
type AntdSelectValue = React.ComponentProps<typeof SelectAntd>['value'];

type SelectValueItem = SelectOption | string | number;

interface TagRenderProps {
  label: React.ReactNode;
  closable?: boolean;
  onClose?: () => void;
}

const tagRender: (props: TagRenderProps) => React.ReactElement = ({ label, closable, onClose }) => {
  const onPreventMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  return (
    <Tag
      className={cn('select-default__tag', { 'select-default__tag_disabled': !closable })}
      closable={closable}
      onMouseDown={onPreventMouseDown}
      onClose={onClose}
      closeIcon={<Clear />}
    >
      {label}
    </Tag>
  );
};

const extraTagRender = (options: SelectOption[]) =>
  options.length ? <Tag className="select-default__tag select-default__tag_extra">+{options.length}</Tag> : null;

const itemValue = (item: SelectValueItem): string | number => (typeof item === 'object' ? item.value : item);

interface SelectInnerProps {
  value?: SelectValueItem[];
  onChange?: (value: unknown, option: unknown) => void;
  onSearch?: (search: string) => void;
  onDropdownVisibleChange?: (open: boolean) => void;
  options?: Array<SelectOption | SelectOptionGroup>;
  loading?: boolean;
  mode?: 'multiple' | 'tags';
  labelInValue?: boolean;
  empty?: JSX.Element | null;
  popupClassName?: string;
  inPopup?: boolean;
  bulkActions?: boolean;
  showSearch?: boolean;
  fetchAllOptions?: () => Promise<SelectOption[]>;
  [key: string]: unknown;
}

export const SelectInner: React.FC<SelectInnerProps> = ({
  value,
  onChange,
  onSearch,
  onDropdownVisibleChange,
  options,
  loading,
  mode,
  labelInValue,
  empty = null,
  popupClassName,
  inPopup,
  bulkActions = true,
  showSearch = true,
  fetchAllOptions,
  ...props
}) => {
  const { t } = useTranslation();
  const [showSelectedOnly, setShowSelectedOnly] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const valueItems = Array.isArray(value) ? value : [];
  const selectedCount = valueItems.length;

  useEffect(() => {
    if (!selectedCount) setShowSelectedOnly(false);
  }, [selectedCount]);

  const isMultiple = mode === SELECT_TYPES.MULTIPLE;
  const withBulkActions = bulkActions && isMultiple;

  const selectedValues = new Set(valueItems.map(itemValue));
  const flatOptions = (options ?? []).flatMap<SelectOption>(o => (isOptionGroup(o) ? o.options : [o]));
  const inOptions = flatOptions.filter(o => selectedValues.has(o.value));
  const seen = new Set(inOptions.map(o => o.value));
  const extraSelected = valueItems
    .filter((v): v is SelectOption => !!v && typeof v === 'object' && !seen.has(v.value))
    .map(({ value: v, label }) => ({ value: v, label }));
  const selectedOptions = [...inOptions, ...extraSelected];

  const baseOptions = loading ? [] : options;
  const displayOptions = showSelectedOnly ? selectedOptions : baseOptions;

  const toValue = (opts: SelectOption[]) =>
    labelInValue ? opts.map(({ value: v, label }) => ({ value: v, label })) : opts.map(o => o.value);

  const handleSearch = (search: string) => {
    setSearchValue(search);
    onSearch?.(search);
  };

  const handleDropdownVisibleChange = (open: boolean) => {
    if (!open) setSearchValue('');
    onDropdownVisibleChange?.(open);
  };

  const onBulkAction = async () => {
    if (selectedCount) {
      onChange?.([], []);
      return;
    }
    if (fetchAllOptions) {
      setActionLoading(true);
      try {
        const all = await fetchAllOptions();
        onChange?.(toValue(all), all);
      } finally {
        setActionLoading(false);
      }
      return;
    }
    const all = flatOptions;
    onChange?.(toValue(all), all);
  };

  const renderFooter = (menu: React.ReactNode) => (
    <>
      {menu}
      {!searchValue && (
        /* onMouseDown preventDefault keeps the dropdown open / input focused while interacting with the footer */
        // eslint-disable-next-line jsx-a11y/no-static-element-interactions
        <div className="select-default__bulk-footer" onMouseDown={e => e.preventDefault()}>
          <span
            className={cn('select-default__bulk-footer__toggle', {
              'select-default__bulk-footer__toggle_disabled': !selectedCount,
            })}
          >
            <Switch
              size="small"
              checked={showSelectedOnly}
              disabled={!selectedCount}
              onChange={checked => setShowSelectedOnly(checked)}
            />
            <Typography.Text>{t('translation:bulk_select.show_only_selected')}</Typography.Text>
          </span>
          <button
            type="button"
            className="select-default__bulk-footer__action"
            disabled={actionLoading || (!selectedCount && !fetchAllOptions && !options?.length)}
            onClick={onBulkAction}
          >
            {selectedCount ? t('translation:bulk_select.clear_all') : t('translation:bulk_select.select_all')}
          </button>
        </div>
      )}
    </>
  );

  return (
    <SelectAntd
      showSearch={showSearch && !showSelectedOnly}
      value={value as AntdSelectValue}
      onChange={(val: unknown, option: unknown) => {
        if (searchValue) setSearchValue('');
        onChange?.(val, option);
      }}
      onSearch={handleSearch}
      onDropdownVisibleChange={handleDropdownVisibleChange}
      popupClassName={cn(
        'select-default__dropdown',
        { 'select-default__dropdown_multiple': isMultiple },
        popupClassName
      )}
      showArrow
      allowClear={isMultiple}
      clearIcon={<Clear />}
      getPopupContainer={() =>
        (inPopup ? document.querySelector('.popup-default__wrap') : document.body) as HTMLElement
      }
      menuItemSelectedIcon={<CheckMark />}
      suffixIcon={<Arrow />}
      maxTagCount="responsive"
      tagRender={tagRender}
      maxTagPlaceholder={extraTagRender}
      placeholder={t('translation:select.placeholder')}
      options={displayOptions as AntdSelectOptions}
      notFoundContent={loading ? <div>Loading...</div> : empty}
      optionFilterProp={labelInValue ? 'label' : 'value'}
      mode={mode}
      labelInValue={labelInValue}
      dropdownRender={withBulkActions ? renderFooter : undefined}
      {...props}
    />
  );
};
